import { Card, Col, Progress, Row, Statistic, Typography } from "antd";
import { BookOutlined, CheckCircleOutlined, StarOutlined } from "@ant-design/icons";
import type { MajorCredits } from "@/api/enrollment";

const { Text } = Typography;

function creditsPercent(earned: number, target: number): number {
  if (target <= 0) return 0;
  return Math.min(100, Math.round((earned / target) * 100));
}

interface MajorCreditsSummaryProps {
  credits: MajorCredits;
}

/** Card "Tiến độ tín chỉ" — tổng tín chỉ đã tích lũy (môn COMPLETED) + còn
 * thiếu bao nhiêu (tách bắt buộc/tự chọn), đối chiếu mục tiêu ngành. Sếp yêu
 * cầu 2026-07-13. `target=0` (admin chưa thiết lập mục tiêu ở
 * `/admin/curriculum`) → hiện "Chưa thiết lập mục tiêu" thay vì thanh 100%
 * gây hiểu lầm "đã xong". */
export function MajorCreditsSummary({ credits }: MajorCreditsSummaryProps) {
  const {
    total_credits_earned,
    required_credits_earned,
    required_credits_target,
    required_credits_remaining,
    elective_credits_earned,
    elective_credits_target,
    elective_credits_remaining,
  } = credits;

  return (
    <Card size="small" title="Tiến độ tín chỉ" style={{ marginBottom: 16 }}>
      <Row gutter={24}>
        <Col xs={24} sm={8}>
          <Statistic
            title="Tổng đã tích lũy"
            value={total_credits_earned}
            suffix="tín chỉ"
            prefix={<BookOutlined />}
            valueStyle={{ color: "#1677ff" }}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Statistic
            title="Bắt buộc"
            value={required_credits_earned}
            suffix={required_credits_target > 0 ? `/ ${required_credits_target}` : undefined}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: "#52c41a" }}
          />
          {required_credits_target > 0 ? (
            <>
              <Progress
                percent={creditsPercent(required_credits_earned, required_credits_target)}
                size="small"
                showInfo={false}
              />
              <Text type="secondary">
                {required_credits_remaining > 0
                  ? `Còn thiếu ${required_credits_remaining} tín chỉ`
                  : "Đã đủ tín chỉ bắt buộc"}
              </Text>
            </>
          ) : (
            <Text type="secondary">Chưa thiết lập mục tiêu</Text>
          )}
        </Col>
        <Col xs={24} sm={8}>
          <Statistic
            title="Tự chọn"
            value={elective_credits_earned}
            suffix={elective_credits_target > 0 ? `/ ${elective_credits_target}` : undefined}
            prefix={<StarOutlined />}
            valueStyle={{ color: "#faad14" }}
          />
          {elective_credits_target > 0 ? (
            <>
              <Progress
                percent={creditsPercent(elective_credits_earned, elective_credits_target)}
                size="small"
                showInfo={false}
                strokeColor="#faad14"
              />
              <Text type="secondary">
                {elective_credits_remaining > 0
                  ? `Còn thiếu ${elective_credits_remaining} tín chỉ`
                  : "Đã đủ tín chỉ tự chọn"}
              </Text>
            </>
          ) : (
            <Text type="secondary">Chưa thiết lập mục tiêu</Text>
          )}
        </Col>
      </Row>
    </Card>
  );
}

export default MajorCreditsSummary;
